import { PATH_URLS } from './pages';
import { adjustRanking, formatNumberValue, getTableHead, sortData, GERMANY_NAME, UNIT_KW, UNIT_MW } from './utils';

function splitByGranularity(data) {
  return {
    country: data.filter((e) => e.granularity === 'COUNTRY'),
    states: data.filter((e) => e.granularity === 'STATE')
  };
}

function splitByCountry(data) {
  return {
    country: data.filter((s) => s.name === GERMANY_NAME),
    states: data.filter((s) => s.name !== GERMANY_NAME)
  };
}

// ---------------------------------------------------------------------------
// futureIndex
// ---------------------------------------------------------------------------

function convertFutureIndex(data, key, country = false) {
  return data.map(({ ags, name, values }, index) => ({
    ags,
    name,
    rowData: [
      name,
      country ? '-' : index + 1,
      values[key] === 0 ? '-' : `${formatNumberValue(values[key], 0, 0)} t`
    ]
  }));
}

export function futureIndex({ data }, { region, pageData }) {
  const dataKey = Object.keys(data[0].values)[0];
  const { country, states } = splitByGranularity(data);

  sortData(states, dataKey);

  return {
    thead: getTableHead(region, pageData.meta.tableHead.region),
    tbody: [
      ...convertFutureIndex(country, dataKey, true),
      ...convertFutureIndex(states, dataKey)
    ]
  };
}

// ---------------------------------------------------------------------------
// electricVehicles
// ---------------------------------------------------------------------------

function parseElectricVehicle(dataItem, index) {
  const { name, ags, values: { amount, co2SavingsGreenElectricity, electricVehicleRatio } } = dataItem;
  return {
    name,
    ags,
    rowData: [
      name,
      name === GERMANY_NAME ? '-' : index + 1,
      electricVehicleRatio ? `${electricVehicleRatio} %` : null,
      formatNumberValue(amount, 0, 2),
      co2SavingsGreenElectricity ? `${formatNumberValue(co2SavingsGreenElectricity, 0, 2)} t` : null
    ].filter(Boolean)
  };
}

export function electricVehicles(apiResponse, { region, pageData }) {
  const { country: germany, states } = splitByCountry(apiResponse.data);

  sortData(states, 'amount');

  const tableHead = apiResponse.data[0].year > 2024
    ? [...getTableHead(region, pageData.meta.tableHead_2025.region)]
    : [...getTableHead(region, pageData.meta.tableHead.region)];

  return {
    thead: tableHead,
    tbody: [
      ...germany.map((item, i) => parseElectricVehicle(item, i)),
      ...adjustRanking(states.map((item, i) => parseElectricVehicle(item, i)), 2, 1)
    ]
  };
}

// ---------------------------------------------------------------------------
// bidiAtlas
// ---------------------------------------------------------------------------

function parseBidiAtlas(dataItem, index) {
  const { name, ags, values: { bidiReadyElectricVehicles, supportedHouseholds } } = dataItem;
  return {
    name,
    ags,
    rowData: [
      name,
      name === GERMANY_NAME ? '-' : index + 1,
      formatNumberValue(bidiReadyElectricVehicles, 0, 0),
      formatNumberValue(supportedHouseholds, 0, 0)
    ].filter(Boolean)
  };
}

export function bidiAtlas(apiResponse, { region, pageData }) {
  const { country: germany, states } = splitByCountry(apiResponse.data);

  sortData(states, 'bidiReadyElectricVehicles');

  return {
    thead: [...getTableHead(region, pageData.meta.tableHead.region)],
    tbody: [
      ...germany.map((item, i) => parseBidiAtlas(item, i)),
      ...adjustRanking(states.map((item, i) => parseBidiAtlas(item, i)), 2, 1)
    ]
  };
}

// ---------------------------------------------------------------------------
// chargers
// ---------------------------------------------------------------------------

function parseCharger(dataItem, index, region) {
  const { name, ags } = dataItem;
  const rank = name === GERMANY_NAME ? '-' : index + 1;
  const points = formatNumberValue(dataItem.values.points, 0, 0);

  let totalPowerRating = dataItem.values.totalPowerRatingMW;
  if (totalPowerRating) {
    totalPowerRating = region === 1
      ? `${formatNumberValue(totalPowerRating * 1000, 0, 0)}&nbsp;${UNIT_KW}`
      : `${formatNumberValue(totalPowerRating)}&nbsp;${UNIT_MW}`;
  }

  const totalPowerRatingKWPer1000 = dataItem.values.totalPowerRatingKWPer1000
    ? `${formatNumberValue(dataItem.values.totalPowerRatingKWPer1000)}&nbsp;${UNIT_KW}`
    : undefined;

  return { name, ags, rowData: [name, rank, points, totalPowerRating, totalPowerRatingKWPer1000] };
}

export function chargers(apiResponse, { region, pageData }) {
  const { country: germany, states } = splitByCountry(apiResponse.data);
  const year2020 = apiResponse.data[0].year === 2020;

  sortData(states, year2020 ? 'stations' : 'points');

  const germanySorted = germany.map((s, i) => parseCharger(s, i, region));
  const statesSorted = states.map((s, i) => parseCharger(s, i, region));
  const rankCol = year2020 ? 3 : 2;

  return {
    thead: getTableHead(region, pageData.meta.tableHead.region),
    tbody: [...germanySorted, ...adjustRanking(statesSorted, rankCol, 1)]
  };
}

// ---------------------------------------------------------------------------
// electricBicycles
// ---------------------------------------------------------------------------

function parseElectricBicycle(dataItem, index, region) {
  const { name, ags, values } = dataItem;
  return {
    name,
    ags,
    rowData: [
      name,
      name === GERMANY_NAME ? '-' : index + 1,
      values.owners ? `${formatNumberValue(values.owners, 0, 2)}%` : null,
      region === 0 && values.potential ? `${formatNumberValue(values.potential, 0, 2)}%` : null,
      region === 0 && values.greenEnergy ? `${formatNumberValue(values.greenEnergy, 0, 2)}%` : null,
      region === 0 && values.flexReadiness ? `${formatNumberValue(values.flexReadiness, 0, 2)}%` : null
    ].filter(Boolean)
  };
}

export function electricBicycles(apiResponse, { region, pageData }) {
  const { country: germany, states } = splitByCountry(apiResponse.data);
  const year = apiResponse.data[0].year;

  sortData(states, 'owners');

  let tableHead;
  if (year === 2023 && pageData.meta.tableHead_2023?.region) {
    tableHead = getTableHead(region, pageData.meta.tableHead_2023.region);
  } else if (year >= 2025 && pageData.meta.tableHead_2025?.region) {
    tableHead = getTableHead(region, pageData.meta.tableHead_2025.region);
  } else {
    tableHead = getTableHead(region, pageData.meta.tableHead.region);
  }

  return {
    thead: tableHead,
    tbody: [
      ...germany.map((item, i) => parseElectricBicycle(item, i, region)),
      ...adjustRanking(states.map((item, i) => parseElectricBicycle(item, i, region)), 2, 1)
    ]
  };
}

// ---------------------------------------------------------------------------
// electricTrucks
// ---------------------------------------------------------------------------

function parseElectricTruck({ ags, name, values: { amount, co2SavingsGreenElectricity } }, index, country = false) {
  return {
    ags,
    name,
    rowData: [
      name,
      country ? '-' : index + 1,
      formatNumberValue(amount, 0, 0),
      `${formatNumberValue(co2SavingsGreenElectricity, 0, 0)} t`
    ]
  };
}

export function electricTrucks({ data }, { region, pageData }) {
  const country = data.filter((e) => e.granularity === 'COUNTRY');
  const states = data.filter((e) => e.granularity === 'STATE');

  sortData(states, 'amount');

  return {
    thead: getTableHead(region, pageData.meta.tableHead.region),
    tbody: [
      ...country.map((e, i) => parseElectricTruck(e, i, true)),
      ...states.map((e, i) => parseElectricTruck(e, i))
    ]
  };
}

// ---------------------------------------------------------------------------
// flexCheck — shared kWh technology converter
// ---------------------------------------------------------------------------

function convertFlexTechnology(data, key, unit, country = false, fractionDigits = 0) {
  return data.map(({ ags, name, values }) => ({
    ags,
    name,
    rowData: [
      name,
      country ? '-' : values.rank,
      values[key] === 0 ? '-' : `${formatNumberValue(values[key], fractionDigits, fractionDigits)} ${unit}`
    ]
  }));
}

function flexTechnology(dataKey, { data }, { region, pageData }, unit = 'kWh', fractionDigits = 0) {
  const { country, states } = splitByGranularity(data);
  return {
    thead: getTableHead(region, pageData.meta.tableHead.region),
    tbody: [
      ...convertFlexTechnology(country, dataKey, unit, true, fractionDigits),
      ...convertFlexTechnology(states, dataKey, unit, false, fractionDigits)
    ]
  };
}

export const flexCheck_technologyTotal = (response, opts) =>
  flexTechnology('totalKWhHousehold', response, opts);

export const flexCheck_technologyHeatpumps = (response, opts) =>
  flexTechnology('heatPumpsKWhHousehold', response, opts);

export const flexCheck_technologyElectricVehicles = (response, opts) =>
  flexTechnology('electricVehiclesKWhHousehold', response, opts);

export const flexCheck_technologyHomeStorage = (response, opts) =>
  flexTechnology('homeStorageKWhHousehold', response, opts);

export const flexCheck_technologyMicroFlexibilities = (response, opts) =>
  flexTechnology('microKWhHousehold', response, opts);

export const flexCheck_financial = (response, opts) =>
  flexTechnology('totalSavings', response, opts, 'Mio. €', 1);

// ---------------------------------------------------------------------------
// flexCheck — flexScore
// ---------------------------------------------------------------------------

export function flexCheck_flexScore({ data }, { region, pageData }) {
  const states = data.filter((e) => e.granularity === 'STATE');
  return {
    thead: getTableHead(region, pageData.meta.tableHead.region),
    tbody: states.map(({ ags, name, values }) => ({
      ags,
      name,
      rowData: [name, values.rank, values.flexScore === 0 ? '-' : values.flexScore]
    }))
  };
}

// ---------------------------------------------------------------------------
// flexCheck — percentage-based parsers (flexKnowledge, flexReadiness)
// ---------------------------------------------------------------------------

function convertFlexPercent(data, key, country = false) {
  return data.map(({ ags, name, values }) => ({
    ags,
    name,
    rowData: [
      name,
      country ? '-' : values.rank,
      values[key] === 0 ? '-' : `${formatNumberValue(values[key], 0, 1)} %`
    ]
  }));
}

function flexPercentParser(dataKey, { data }, { region, pageData }) {
  const { country, states } = splitByGranularity(data);
  return {
    thead: getTableHead(region, pageData.meta.tableHead.region),
    tbody: [
      ...convertFlexPercent(country, dataKey, true),
      ...convertFlexPercent(states, dataKey)
    ]
  };
}

export const flexCheck_flexKnowledge = (response, opts) =>
  flexPercentParser('flexKnowledge', response, opts);

export const flexCheck_flexReadiness = (response, opts) =>
  flexPercentParser('flexReadiness', response, opts);

// ---------------------------------------------------------------------------
// getTableParser — replaces TableDataParser class
// ---------------------------------------------------------------------------

const PARSER_MAP = {
  [PATH_URLS.ZUKUNFTSINDEX.CO2_EINSPARUNG_JAHR]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_ENERGIEWENDE_ZUHAUSE.DURCH_ENERGIEWENDE_ZUHAUSE_GESAMT]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_ENERGIEWENDE_ZUHAUSE.DURCH_ENERGIEWENDE_ZUHAUSE_INSTALLATION_DACH_SOLARANLAGE]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_ENERGIEWENDE_ZUHAUSE.DURCH_ENERGIEWENDE_ZUHAUSE_INSTALLATION_BALKON_SOLARANLAGE]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_ENERGIEWENDE_ZUHAUSE.DURCH_ENERGIEWENDE_ZUHAUSE_EFFIZIENTE_HEIZANLAGE]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_VERKEHRSWENDE.DURCH_VERKEHRSWENDE_GESAMT]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_VERKEHRSWENDE.DURCH_VERKEHRSWENDE_UMSTIEG_E_AUTO]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_VERKEHRSWENDE.DURCH_VERKEHRSWENDE_UMSTIEG_OEPNV]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_VERKEHRSWENDE.DURCH_VERKEHRSWENDE_BIDIREKTIONALES_LADEN]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_VERBRAUCHSWENDE.DURCH_VERBRAUCHSWENDE_GESAMT]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_VERBRAUCHSWENDE.DURCH_VERBRAUCHSWENDE_HEIZTEMPERATUR_SENKEN]: futureIndex,
  [PATH_URLS.ZUKUNFTSINDEX.DURCH_VERBRAUCHSWENDE.DURCH_VERBRAUCHSWENDE_SMARTE_THERMOSTATE]: futureIndex,
  [PATH_URLS.E_MOBILITY.E_AUTO_BESITZER]: electricVehicles,
  [PATH_URLS.E_MOBILITY.BIDI_ATLAS]: bidiAtlas,
  [PATH_URLS.E_MOBILITY.LADESTATIONEN]: chargers,
  [PATH_URLS.E_MOBILITY.E_BIKES]: electricBicycles,
  [PATH_URLS.E_MOBILITY.E_LKW]: electricTrucks,
  [PATH_URLS.FLEX_CHECK.FLEX_SCORE]: flexCheck_flexScore,
  [PATH_URLS.FLEX_CHECK.FINANZIELL]: flexCheck_financial,
  [PATH_URLS.FLEX_CHECK.TECHNOLOGIE.GESAMT]: flexCheck_technologyTotal,
  [PATH_URLS.FLEX_CHECK.TECHNOLOGIE.WAERMEPUMPEN]: flexCheck_technologyHeatpumps,
  [PATH_URLS.FLEX_CHECK.TECHNOLOGIE.E_AUTOS]: flexCheck_technologyElectricVehicles,
  [PATH_URLS.FLEX_CHECK.TECHNOLOGIE.HEIMSPEICHER]: flexCheck_technologyHomeStorage,
  [PATH_URLS.FLEX_CHECK.TECHNOLOGIE.MIKRO_FLEXIBILITAETEN]: flexCheck_technologyMicroFlexibilities,
  [PATH_URLS.FLEX_CHECK.WISSEN_UND_BEREITSCHAFT.FLEX_WISSEN]: flexCheck_flexKnowledge,
  [PATH_URLS.FLEX_CHECK.WISSEN_UND_BEREITSCHAFT.FLEX_BEREITSCHAFT]: flexCheck_flexReadiness
};

export function getTableParser({ data, pageData, region }) {
  const path = pageData?.path;
  const parser = PARSER_MAP[path];
  if (!parser) {
    throw new Error(`No table parser found for path: "${path}"`);
  }
  return parser(data, { region, pageData });
}
